import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Api from '../Api/Api.json';
import SimpleHealthChart from './SimpleHealthChart';
import { useWellnessSummaryData } from '../hooks/wellnessSummaryData';

const VisualizaSummaryData = () => {
  const navigate = useNavigate();
  const dateScrollerRef = useRef(null);

  const [patientId, setPatientId] = useState(null);
  const [healthData, setHealthData] = useState({
    step: {},
    heartrate: {},
    hrv: {}
  });

  // Daily metrics data (0-100%)
  const [dailyMetrics, setDailyMetrics] = useState({
    activity: 90,
    sleep: 85,
    nutrition: 70,
    stress: 75,
    cardio: 60
  });

  // Sleep score list from API
  const [sleepScoreList, setSleepScoreList] = useState([]);

  // Generate last 7 dates starting from yesterday (excluding today)
  const getLast7Dates = () => {
    const today = new Date();
    const dates = [];
    for (let i = 7; i >= 1; i--) {
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      dates.push(date.toISOString().split('T')[0]); // YYYY-MM-DD format
    }
    return dates;
  };

  const last7Dates = getLast7Dates();
  const [selectedDate, setSelectedDate] = useState(last7Dates[last7Dates.length - 1]); // Default to latest date

  // Scroll to selected date on mount and when date changes
  useEffect(() => {
    if (!dateScrollerRef.current) return;
    const selectedButton = dateScrollerRef.current.querySelector(`[data-date="${selectedDate}"]`);
    if (selectedButton) {
      selectedButton.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  }, [selectedDate]);

  // Format date for display
  const formatDateDisplay = (dateStr) => {
    const date = new Date(dateStr);
    const month = date.toLocaleString('default', { month: 'short' });
    const day = date.getDate();
    return `${month} ${day}`;
  };

  const isToday = (dateStr) => {
    const today = new Date().toISOString().split('T')[0];
    return dateStr === today;
  };

  // Feedback system state
  const [isFeedbackDialogOpen, setIsFeedbackDialogOpen] = useState(false);
  const [feedbackText, setFeedbackText] = useState("");
  const [backendReply, setBackendReply] = useState("");
  const [hasNewReply, setHasNewReply] = useState(false);
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
  const [lastSeenReply, setLastSeenReply] = useState(""); // Track the last reply the user has seen

  // Daily Insight modal state
  const [showInsightModal, setShowInsightModal] = useState(false);
  const [insightText, setInsightText] = useState("");
  const [isLoadingInsight, setIsLoadingInsight] = useState(false);

  // Get patient ID from localStorage
  useEffect(() => {
    if (localStorage.getItem('user_info') !== null) {
      const userInfo = JSON.parse(localStorage.getItem('user_info'));
      setPatientId(userInfo.di_id);
    }
  }, []);

  // Call the wellness summary API
  const { data: wellnessData, isLoading, error } = useWellnessSummaryData(patientId);

  // Fetch sleep score list from API
  useEffect(() => {
    if (!patientId) return;

    const headers = { headers: { "Content-Type": "application/json" } };
    const url = Api.FITNESS_API;
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const requestData = {
      task: 'getSemionWellnessData',
      id: patientId,
      timezone: userTimezone,
    };

    axios.post(url, requestData, headers)
      .then(result => {
        if (result.data && result.data.sleepScoreList) {
          setSleepScoreList(result.data.sleepScoreList);
        }
      })
      .catch(() => {});
  }, [patientId]);

  // Update sleep metric based on selected date
  useEffect(() => {
    if (!sleepScoreList || sleepScoreList.length === 0) return;
    const sleepScoreEntry = sleepScoreList.find(item => item.date === selectedDate);

    if (sleepScoreEntry && sleepScoreEntry.y !== null && sleepScoreEntry.y !== undefined) {
      setDailyMetrics(prev => ({ ...prev, sleep: sleepScoreEntry.y }));
    } else {
      setDailyMetrics(prev => ({ ...prev, sleep: 0 }));
    }
  }, [selectedDate, sleepScoreList]);

  // Extract stress scores from wellnessData and map to dates
  const getStressScoreForDate = (dateStr) => {
    if (!wellnessData || !wellnessData.stress || !wellnessData.stress.week) {
      return null;
    }

    const stressWeekData = wellnessData.stress.week;
    for (let i = 0; i < stressWeekData.length; i++) {
      const dayData = stressWeekData[i];
      const dateKey = Object.keys(dayData)[0];

      if (dateKey === dateStr) {
        const dataObj = dayData[dateKey];
        if (dataObj && dataObj.avg !== null && dataObj.avg !== undefined) {
          return parseFloat(dataObj.avg);
        }
      }
    }
    return null;
  };

  useEffect(() => {
    const stressScore = getStressScoreForDate(selectedDate);
    if (stressScore !== null) {
      setDailyMetrics(prev => ({ ...prev, stress: stressScore }));
    } else {
      setDailyMetrics(prev => ({ ...prev, stress: 0 }));
    }
  }, [selectedDate, wellnessData]);

  const getActivityScoreForDate = (dateStr) => {
    if (!wellnessData || !wellnessData.dailysteps || !wellnessData.dailysteps.week) {
      return null;
    }

    const dailystepsWeekData = wellnessData.dailysteps.week;
    for (let i = 0; i < dailystepsWeekData.length; i++) {
      const dayData = dailystepsWeekData[i];
      const dateKey = Object.keys(dayData)[0];

      if (dateKey === dateStr) {
        const dataObj = dayData[dateKey];
        if (dataObj && dataObj.avg !== null && dataObj.avg !== undefined) {
          return Math.min((parseFloat(dataObj.avg) * 100) / 7000, 100);
        }
      }
    }

    return null;
  };

  useEffect(() => {
    const activityScore = getActivityScoreForDate(selectedDate);
    if (activityScore !== null) {
      setDailyMetrics(prev => ({ ...prev, activity: activityScore }));
    } else {
      setDailyMetrics(prev => ({ ...prev, activity: 0 }));
    }
  }, [selectedDate, wellnessData]);

  const getCardioScoreForDate = (dateStr) => {
    if (!wellnessData || !wellnessData.heartrate || !wellnessData.heartrate.week) {
      return null;
    }

    const heartrateWeekData = wellnessData.heartrate.week;
    for (let i = 0; i < heartrateWeekData.length; i++) {
      const dayData = heartrateWeekData[i];
      const dateKey = Object.keys(dayData)[0];

      if (dateKey === dateStr) {
        const dataObj = dayData[dateKey];
        if (dataObj && dataObj.avg !== null && dataObj.avg !== undefined) {
          const cardioScore = parseFloat(dataObj.avg) - 55;
          return Math.max(cardioScore, 0);
        }
      }
    }

    return null;
  };

  useEffect(() => {
    const cardioScore = getCardioScoreForDate(selectedDate);
    if (cardioScore !== null) {
      setDailyMetrics(prev => ({ ...prev, cardio: cardioScore }));
    } else {
      setDailyMetrics(prev => ({ ...prev, cardio: 0 }));
    }
  }, [selectedDate, wellnessData]);

  const fetchWellnessInsights = async () => {
    if (!patientId) return;

    setIsLoadingInsight(true);
    setShowInsightModal(true);
    setInsightText("");

    try {
      const headers = { headers: { "Content-Type": "application/json" } };
      const url = Api.RECOMMENDATIONS_TEXT_API;
      const requestData = {
        task: "getWellnessInsightsNLPForUser",
        user_id: patientId
      };

      const response = await axios.post(url, requestData, headers);

      let responseText = response.data;
      if (typeof responseText === 'object') {
        responseText = responseText.text || responseText.message || responseText.insight || JSON.stringify(responseText);
      }

      setInsightText(responseText || "No insights available.");
    } catch (e) {
      setInsightText("Failed to load insights. Please try again later.");
    } finally {
      setIsLoadingInsight(false);
    }
  };

  // Transform API data to the required format for last 8 days
  const transformApiData = (apiData) => {
    if (!apiData) return { step: {}, heartrate: {}, hrv: {} };

    // Generate the last 8 days starting from today
    const today = new Date();
    const last8Days = [];
    for (let i = 7; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      last8Days.push(date.toISOString().split('T')[0]); // YYYY-MM-DD format
    }

    const transformedData = {
      step: {},
      heartrate: {},
      hrv: {}
    };

    // Initialize all 8 days with empty objects (not null)
    last8Days.forEach(dateKey => {
      transformedData.step[dateKey] = { avg: null, min: null, max: null };
      transformedData.heartrate[dateKey] = { avg: null, min: null, max: null };
      transformedData.hrv[dateKey] = { avg: null, min: null, max: null };
    });

    // Transform step data (using dailysteps)
    if (apiData.dailysteps && apiData.dailysteps.week) {
      apiData.dailysteps.week.forEach((dayData) => {
        const dateKey = Object.keys(dayData)[0];
        const dataObj = dayData[dateKey];
        // Only include data for the last 8 days
        if (last8Days.includes(dateKey) && dataObj && dataObj.avg !== null && dataObj.avg !== undefined) {
          transformedData.step[dateKey] = {
            avg: parseFloat(dataObj.avg),
            min: dataObj.min !== null && dataObj.min !== undefined ? parseFloat(dataObj.min) : null,
            max: dataObj.max !== null && dataObj.max !== undefined ? parseFloat(dataObj.max) : null
          };
        }
      });
    }

    // Transform heart rate data
    if (apiData.heartrate && apiData.heartrate.week) {
      apiData.heartrate.week.forEach((dayData) => {
        const dateKey = Object.keys(dayData)[0];
        const dataObj = dayData[dateKey];
        // Only include data for the last 8 days
        if (last8Days.includes(dateKey) && dataObj && dataObj.avg !== null && dataObj.avg !== undefined) {
          transformedData.heartrate[dateKey] = {
            avg: parseFloat(dataObj.avg),
            min: dataObj.min !== null && dataObj.min !== undefined ? parseFloat(dataObj.min) : null,
            max: dataObj.max !== null && dataObj.max !== undefined ? parseFloat(dataObj.max) : null
          };
        }
      });
    }

    // Transform HRV data
    if (apiData.hrv && apiData.hrv.week) {
      apiData.hrv.week.forEach((dayData) => {
        const dateKey = Object.keys(dayData)[0];
        const dataObj = dayData[dateKey];
        // Only include data for the last 8 days
        if (last8Days.includes(dateKey) && dataObj && dataObj.avg !== null && dataObj.avg !== undefined) {
          const hrvData = {
            avg: parseFloat(dataObj.avg),
            min: dataObj.min !== null && dataObj.min !== undefined ? parseFloat(dataObj.min) : null,
            max: dataObj.max !== null && dataObj.max !== undefined ? parseFloat(dataObj.max) : null
          };
          transformedData.hrv[dateKey] = hrvData;
        }
      });
    }

    return transformedData;
  };

  // Update health data when API data changes
  useEffect(() => {
    if (wellnessData) {
      const transformedData = transformApiData(wellnessData);
      setHealthData(transformedData);
    }
  }, [wellnessData]);

  // Get appropriate unit and title for the data type
  const getDataTypeInfo = (type) => {
    switch (type) {
      case 'step':
        return { unit: 'steps', title: 'Daily Steps', color: '#3B82F6' };
      case 'heartrate':
        return { unit: 'bpm', title: 'Heart Rate', color: '#EF4444' };
      case 'hrv':
        return { unit: 'ms', title: 'Heart Rate Variability', color: '#10B981' };
      default:
        return { unit: '', title: 'Data', color: '#6B7280' };
    }
  };

  // Calculate statistics for each data type
  const calculateStats = (data) => {
    if (!data || Object.keys(data).length === 0) return null;
    
    const values = Object.values(data).map(d => d.avg).filter(v => v !== null && v !== undefined);
    const minValues = Object.values(data).map(d => d.min).filter(v => v !== null && v !== undefined);
    const maxValues = Object.values(data).map(d => d.max).filter(v => v !== null && v !== undefined);
    
    if (values.length === 0) return null;
    
    // Calculate average
    const avg = Math.round(values.reduce((sum, val) => sum + val, 0) / values.length);
    
    // Calculate min and max, handling empty arrays
    const min = minValues.length > 0 ? Math.min(...minValues) : avg;
    const max = maxValues.length > 0 ? Math.max(...maxValues) : avg;
    
    return {
      avg: avg,
      min: min,
      max: max,
      trend: values[values.length - 1] > values[0] ? 'up' : values[values.length - 1] < values[0] ? 'down' : 'stable'
    };
  };

  const stepStats = calculateStats(healthData.step);
  const hrStats = calculateStats(healthData.heartrate);
  const hrvStats = calculateStats(healthData.hrv);

  // Feedback functions
  const handleOpenFeedbackDialog = () => {
    setIsFeedbackDialogOpen(true);
    setLastSeenReply(backendReply); // Mark current reply as seen
    setHasNewReply(false); // Clear notification when dialog is opened
  };

  const handleCloseFeedbackDialog = () => {
    setIsFeedbackDialogOpen(false);
  };

  const handleSubmitFeedback = async () => {
    if (!feedbackText.trim()) return;
    
    setIsSubmittingFeedback(true);
    try {
      // TODO: Replace with actual API call
      console.log("Submitting feedback:", feedbackText);
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Simulate backend reply (replace with actual API response)
      const mockReply = "Thank you for your feedback! We'll review your health data and get back to you with personalized recommendations.";
      setBackendReply(mockReply);
      
      // Clear feedback text
      setFeedbackText("");
      
      // Close dialog
      setIsFeedbackDialogOpen(false);
      
    } catch (error) {
      console.error("Error submitting feedback:", error);
    } finally {
      setIsSubmittingFeedback(false);
    }
  };

  // Check for new replies (this would typically be called from an API or WebSocket)
  useEffect(() => {
    // Show notification badge only if:
    // 1. There is a backend reply
    // 2. The reply is different from what the user last saw
    // 3. The dialog is not currently open
    if (backendReply && backendReply !== lastSeenReply && !isFeedbackDialogOpen) {
      setHasNewReply(true);
    } else {
      setHasNewReply(false);
    }
  }, [backendReply, lastSeenReply, isFeedbackDialogOpen]);

  if (isLoading) {
    return (
      <div className="relative min-h-screen pb-20">
        <div className="absolute inset-0 bg-black"></div>

        <div className="container mx-auto px-4 py-6 relative z-10" style={{ paddingTop: 'max(2.5rem, env(safe-area-inset-top, 2.5rem))' }}>
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-white mb-2">Health Data Summary</h1>
            <p className="text-white">Loading health data...</p>
          </div>
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-2 border-white border-t-transparent"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="relative min-h-screen pb-20">
        <div className="absolute inset-0 bg-black"></div>

        <div className="container mx-auto px-4 py-6 relative z-10" style={{ paddingTop: 'max(2.5rem, env(safe-area-inset-top, 2.5rem))' }}>
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-white mb-2">Health Data Summary</h1>
            <p className="text-white">Error loading health data</p>
          </div>
          <div className="text-center text-red-400">
            <p>Unable to load health data. Please try again later.</p>
            <p className="text-sm mt-2">Error: {error.message}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-20">
      {/* Black background */}
      <div className="absolute inset-0 bg-black"></div>
      
      <div className="container mx-auto px-4 py-6 relative z-10">
        {/* Header */}
        <div className="mb-8 text-center">
          <h1 className="text-2xl font-bold text-white mb-2">energyBand</h1>
          <p className="text-white">Monitor, take action, improve</p>
        </div>

        {/* Daily Metrics Card */}
        <div className="bg-[#161B22] rounded-lg shadow-md p-6 mb-8 border border-[#30363D]">
          <h3 className="text-lg font-semibold text-white mb-4">Daily Metrics</h3>
          
          {/* Date Scroller */}
          <div className="mb-6">
            <div 
              ref={dateScrollerRef}
              className="flex overflow-x-auto gap-2 pb-2"
              style={{ 
                scrollbarWidth: 'none', 
                msOverflowStyle: 'none',
                WebkitScrollbar: { display: 'none' }
              }}
            >
              <style>{`
                div::-webkit-scrollbar {
                  display: none;
                }
              `}</style>
              {last7Dates.map((dateStr) => {
                const isSelected = selectedDate === dateStr;
                return (
                  <button
                    key={dateStr}
                    data-date={dateStr}
                    onClick={() => setSelectedDate(dateStr)}
                    className={`flex-shrink-0 px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                      isSelected
                        ? 'bg-blue-600 text-white shadow-md'
                        : 'bg-blue-300 text-black hover:bg-blue-400'
                    }`}
                  >
                    <div className="text-sm whitespace-nowrap">
                      {formatDateDisplay(dateStr)}
                      {isToday(dateStr) && (
                        <span className="ml-1 text-xs opacity-75">(Today)</span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex flex-col gap-6">
            {/* Top row: Activity, Sleep, Nutrition */}
            <div className="grid grid-cols-3 gap-4">
              {[
                { 
                  key: 'activity', 
                  label: 'Activity', 
                  navigate: () => navigate('/landing/tab3/VisualizeWellnessData', { state: { dataType: 'dailysteps' } })
                },
                { 
                  key: 'sleep', 
                  label: 'Sleep', 
                  navigate: () => navigate('/health-categories/sleep')
                },
                { 
                  key: 'nutrition', 
                  label: 'Nutrition', 
                  navigate: () => navigate('/health-categories/nutrition')
                }
              ].map((metric) => {
              const value = dailyMetrics[metric.key];
              const percentage = Math.round(value);
              const radius = 40;
              const circumference = 2 * Math.PI * radius;
              const offset = circumference - (value / 100) * circumference;
              
              return (
                <div key={metric.key} className="flex flex-col items-center">
                  {/* Circular Progress */}
                  <div className="relative w-24 h-24 mb-2">
                    <svg className="transform -rotate-90 w-24 h-24">
                      {/* Background circle */}
                      <circle
                        cx="48"
                        cy="48"
                        r={radius}
                        stroke="#E5E7EB"
                        strokeWidth="8"
                        fill="none"
                      />
                      {/* Progress circle */}
                      <circle
                        cx="48"
                        cy="48"
                        r={radius}
                        stroke={value >= 80 ? "#10B981" : value >= 60 ? "#F59E0B" : "#EF4444"}
                        strokeWidth="8"
                        fill="none"
                        strokeDasharray={circumference}
                        strokeDashoffset={offset}
                        strokeLinecap="round"
                        className="transition-all duration-300"
                      />
                    </svg>
                    {/* Percentage text in center */}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-center">
                        <div className="text-lg font-bold text-white">{percentage}</div>
                        <div className="text-xs text-white">%</div>
                      </div>
                    </div>
                  </div>
                  {/* Label with arrow */}
                  <div className="flex items-center gap-1 cursor-pointer" onClick={metric.navigate}>
                    <span className="text-sm text-white font-medium">{metric.label}</span>
                    <span className="text-white">›</span>
                  </div>
                </div>
              );
            })}
            </div>
            {/* Bottom row: Stress, Cardio */}
            <div className="grid grid-cols-2 gap-4 justify-center max-w-md mx-auto">
              {[
                { 
                  key: 'stress', 
                  label: 'Stress', 
                  navigate: () => navigate('/landing/tab3/VisualizeWellnessData', { state: { dataType: 'stress' } })
                },
                { 
                  key: 'cardio', 
                  label: 'Cardio', 
                  navigate: () => navigate('/landing/tab3/VisualizeWellnessData', { state: { dataType: 'heartrate' } })
                }
              ].map((metric) => {
                const value = dailyMetrics[metric.key];
                const percentage = Math.round(value);
                const radius = 40;
                const circumference = 2 * Math.PI * radius;
                const offset = circumference - (value / 100) * circumference;
                
                return (
                  <div key={metric.key} className="flex flex-col items-center">
                    {/* Circular Progress */}
                    <div className="relative w-24 h-24 mb-2">
                      <svg className="transform -rotate-90 w-24 h-24">
                        {/* Background circle */}
                        <circle
                          cx="48"
                          cy="48"
                          r={radius}
                          stroke="#E5E7EB"
                          strokeWidth="8"
                          fill="none"
                        />
                        {/* Progress circle */}
                        <circle
                          cx="48"
                          cy="48"
                          r={radius}
                          stroke={value >= 80 ? "#10B981" : value >= 60 ? "#F59E0B" : "#EF4444"}
                          strokeWidth="8"
                          fill="none"
                          strokeDasharray={circumference}
                          strokeDashoffset={offset}
                          strokeLinecap="round"
                          className="transition-all duration-300"
                        />
                      </svg>
                      {/* Percentage text in center */}
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="text-center">
                          <div className="text-lg font-bold text-white">{percentage}</div>
                          <div className="text-xs text-white">%</div>
                        </div>
                      </div>
                    </div>
                    {/* Label with arrow */}
                    <div className="flex items-center gap-1 cursor-pointer" onClick={metric.navigate}>
                      <span className="text-sm text-white font-medium">{metric.label}</span>
                      <span className="text-white">›</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {/* Daily Insight Card */}
          <div className="bg-[#161B22] rounded-lg shadow-md pt-2 pb-1 px-6 border border-[#30363D]">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-3">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-6 h-6"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#58A6FF"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 2L2 7l10 5 10-5-10-5z" />
                  <path d="M2 17l10 5 10-5M2 12l10 5 10-5" />
                </svg>
                <h3 className="text-lg font-semibold text-[#F0F6FC]" style={{ fontFamily: 'Orbitron, sans-serif' }}>Daily Insight</h3>
              </div>
              <button
                onClick={fetchWellnessInsights}
                className="text-[#58A6FF] hover:text-[#79C0FF] transition-colors cursor-pointer"
                disabled={isLoadingInsight}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-6 h-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>

          {/* Steps Card */}
          <div className="bg-[#161B22] rounded-lg shadow-md pt-2 pb-1 px-6 border border-[#30363D]">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-3">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-6 h-6"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#58A6FF"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
                </svg>
                <h3 className="text-lg font-semibold text-[#F0F6FC]" style={{ fontFamily: 'Orbitron, sans-serif' }}>Daily Steps</h3>
              </div>
            </div>
            {stepStats && (
              <div className="space-y-1 mb-1">
                <div className="flex justify-between">
                  <span className="text-[#8B949E]" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Average:</span>
                  <span className="font-semibold text-[#F0F6FC]" style={{ fontFamily: 'Inter, sans-serif' }}>{stepStats.avg.toLocaleString()} steps</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#8B949E]" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Range:</span>
                  <span className="font-semibold text-[#F0F6FC]" style={{ fontFamily: 'Inter, sans-serif' }}>{stepStats.min.toLocaleString()} - {stepStats.max.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#8B949E]" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Trend:</span>
                  <span className={`font-semibold ${
                    stepStats.trend === 'up' ? 'text-[#D29922]' : 
                    stepStats.trend === 'down' ? 'text-[#3FB950]' : 'text-[#8B949E]'
                  }`} style={{ fontFamily: 'Inter, sans-serif' }}>
                    {stepStats.trend === 'up' ? 'Increasing' : 
                     stepStats.trend === 'down' ? 'Decreasing' : 'Stable'}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Steps Chart */}
          <div className="h-48 w-full -mx-10 -mt-2">
            <SimpleHealthChart
              dataType="step"
              unit="steps"
              data={healthData.step}
              color="#58A6FF"
              height={180}
              darkTheme={true}
            />
          </div>

          {/* Heart Rate Card */}
          <div className="bg-[#161B22] rounded-lg shadow-md pt-2 pb-1 px-6 border border-[#30363D]">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-3">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-6 h-6"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#58A6FF"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l9 9 9-9a5.5 5.5 0 0 0 0-7.8z" />
                </svg>
                <h3 className="text-lg font-semibold text-[#F0F6FC]" style={{ fontFamily: 'Orbitron, sans-serif' }}>Heart Rate</h3>
              </div>
            </div>
            {hrStats && (
              <div className="space-y-1 mb-1">
                <div className="flex justify-between">
                  <span className="text-[#8B949E]" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Average:</span>
                  <span className="font-semibold text-[#F0F6FC]" style={{ fontFamily: 'Inter, sans-serif' }}>{hrStats.avg} bpm</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#8B949E]" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Range:</span>
                  <span className="font-semibold text-[#F0F6FC]" style={{ fontFamily: 'Inter, sans-serif' }}>{hrStats.min} - {hrStats.max} bpm</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#8B949E]" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Trend:</span>
                  <span className={`font-semibold ${
                    hrStats.trend === 'up' ? 'text-[#D29922]' : 
                    hrStats.trend === 'down' ? 'text-[#3FB950]' : 'text-[#8B949E]'
                  }`} style={{ fontFamily: 'Inter, sans-serif' }}>
                    {hrStats.trend === 'up' ? 'Increasing' : 
                     hrStats.trend === 'down' ? 'Decreasing' : 'Stable'}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Heart Rate Chart */}
          <div className="h-48 w-full -mx-10 -mt-2">
            <SimpleHealthChart
              dataType="heartrate"
              unit="bpm"
              data={healthData.heartrate}
              color="#58A6FF"
              height={180}
              darkTheme={true}
            />
          </div>

          {/* HRV Card */}
          <div className="bg-[#161B22] rounded-lg shadow-md pt-2 pb-1 px-6 border border-[#30363D]">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-3">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-6 h-6"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#58A6FF"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
                  <path d="M21 3a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
                  <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 8" />
                  <path d="M3 21a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 16" />
                </svg>
                <h3 className="text-lg font-semibold text-[#F0F6FC]" style={{ fontFamily: 'Orbitron, sans-serif' }}>Heart Rate Variability</h3>
              </div>
            </div>
            {hrvStats && (
              <div className="space-y-1 mb-1">
                <div className="flex justify-between">
                  <span className="text-[#8B949E]" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Average:</span>
                  <span className="font-semibold text-[#F0F6FC]" style={{ fontFamily: 'Inter, sans-serif' }}>{hrvStats.avg} ms</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#8B949E]" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Range:</span>
                  <span className="font-semibold text-[#F0F6FC]" style={{ fontFamily: 'Inter, sans-serif' }}>{hrvStats.min} - {hrvStats.max} ms</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#8B949E]" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Trend:</span>
                  <span className={`font-semibold ${
                    hrvStats.trend === 'up' ? 'text-[#D29922]' : 
                    hrvStats.trend === 'down' ? 'text-[#3FB950]' : 'text-[#8B949E]'
                  }`} style={{ fontFamily: 'Inter, sans-serif' }}>
                    {hrvStats.trend === 'up' ? 'Increasing' : 
                     hrvStats.trend === 'down' ? 'Decreasing' : 'Stable'}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* HRV Chart */}
          <div className="h-48 w-full -mx-10 -mt-2 mb-20">
            <SimpleHealthChart
              dataType="hrv"
              unit="ms"
              data={healthData.hrv}
              color="#58A6FF"
              height={180}
              darkTheme={true}
            />
          </div>
        </div>

        {/* Feedback Dialog */}
        {isFeedbackDialogOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-[#161B22] rounded-lg shadow-xl max-w-md w-full max-h-[80vh] overflow-y-auto border border-[#30363D]">
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-white">Provide Feedback</h3>
                  <button
                    onClick={handleCloseFeedbackDialog}
                    className="text-gray-400 hover:text-gray-600 text-xl"
                  >
                    ×
                  </button>
                </div>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-white mb-2">
                    Your feedback about your health data:
                  </label>
                  <textarea
                    value={feedbackText}
                    onChange={(e) => setFeedbackText(e.target.value)}
                    placeholder="Share your thoughts, questions, or concerns about your health data..."
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    rows={4}
                  />
                </div>

                {/* Show backend reply in dialog if available */}
                {backendReply && (
                  <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-green-800 mb-2">Previous Response:</h4>
                    <p className="text-green-700 text-sm">{backendReply}</p>
                  </div>
                )}

                <div className="flex gap-3">
                  <button
                    onClick={handleCloseFeedbackDialog}
                    className="flex-1 px-4 py-2 border border-gray-300 text-white rounded-lg hover:bg-gray-50 transition-colors duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSubmitFeedback}
                    disabled={!feedbackText.trim() || isSubmittingFeedback}
                    className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors duration-200"
                  >
                    {isSubmittingFeedback ? "Submitting..." : "Submit Feedback"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Daily Insight Modal */}
        {showInsightModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-[#161B22] rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-[#30363D]">
              {/* Modal Header */}
              <div className="sticky top-0 bg-[#161B22] border-b border-[#30363D] px-6 py-4 flex items-center justify-between">
                <h2 className="text-2xl font-bold text-white">Daily Insight</h2>
                <button
                  onClick={() => setShowInsightModal(false)}
                  className="text-white hover:text-gray-400 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-6">
                {isLoadingInsight ? (
                  <div className="text-center py-8">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-2 border-[#58A6FF] border-t-transparent mb-4"></div>
                    <p className="text-white">Loading insights...</p>
                  </div>
                ) : insightText ? (
                  <div className="text-white whitespace-pre-wrap leading-relaxed">
                    {insightText}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-white">No insights available.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VisualizaSummaryData; 